import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "../css/ProfileAcademy.css";
import "../css/calendar.css";

const BACKEND_URL = "https://linxlegacyacademy.dk";

// ─── Kategori-farver ──────────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  træning:   { color: "#7F77DD", label: "Træning" },
  turnering: { color: "#D85A30", label: "Turnering" },
  møde:      { color: "#1D9E75", label: "Møde" },
  event:     { color: "#378ADD", label: "Event" },
  andet:     { color: "#888780", label: "Andet" },
};

function getCategoryStyle(kategori) {
  const key = (kategori || "andet").toLowerCase();
  return CATEGORY_COLORS[key] || CATEGORY_COLORS["andet"];
}

// ─── Felter ───────────────────────────────────────────────────────────────────
const PERSONAL_FIELDS = {
  personal_id: "Personligt ID",
  discord_id:  "Discord ID",
  username:    "Brugernavn",
  email:       "E-mail",
  phone:       "Telefon",
  ingame_name: "Ingame Navn",
};
const EDITABLE_FIELDS = ["email", "phone", "ingame_name"];

const STATUS_MAP = {
  active:     { label: "Betalt ✅",                className: "paid" },
  paid:       { label: "Betalt ✅",                className: "paid" },
  past_due:   { label: "Betaling fejlet ⚠️",       className: "past_due" },
  canceled:   { label: "Annulleret ❌",            className: "canceled" },
  manual:     { label: "Manuel behandling 🕒",     className: "manual" },
  incomplete: { label: "Ufuldstændig betaling ⚠️", className: "incomplete" },
  trialing:   { label: "Prøveperiode ⏳",          className: "trialing" },
  unpaid:     { label: "Ikke betalt ❌",           className: "unpaid" },
};

// ─── Legend ───────────────────────────────────────────────────────────────────
function CalendarLegend() {
  return (
    <div className="cal-legend">
      {Object.values(CATEGORY_COLORS).map(({ color, label }) => (
        <div key={label} className="cal-legend-item">
          <span className="cal-legend-dot" style={{ backgroundColor: color }} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Opret-modal ──────────────────────────────────────────────────────────────
function CreateModal({ date, teams, defaultTeam, onClose, onSave }) {
  const [titel, setTitel] = useState("");
  const [kategori, setKategori] = useState("træning");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [beskrivelse, setBeskrivelse] = useState("");
  const [teamId, setTeamId] = useState(defaultTeam || teams?.[0]?.id || "");

  const handleSave = () => {
    if (!titel.trim()) return;
    if (!teamId) { alert("Vælg hvilket hold begivenheden skal på"); return; }
    onSave({
      titel, kategori,
      start: `${date}T${startTime}`,
      slut: `${date}T${endTime}`,
      beskrivelse,
      team_id: teamId,
    });
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Ny begivenhed</h3>

        <div className="cal-modal-field">
          <label>Titel</label>
          <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Tilføj titel..." autoFocus />
        </div>

        {/* Hold-vælger – hvor skal begivenheden hen */}
        <div className="cal-modal-field">
          <label>Hold</label>
          <select value={teamId} onChange={(e) => setTeamId(Number(e.target.value))}>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="cal-modal-field">
          <label>Dato</label>
          <input type="date" value={date} readOnly />
        </div>

        <div className="cal-modal-row">
          <div className="cal-modal-field">
            <label>Start</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="cal-modal-field">
            <label>Slut</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>

        <div className="cal-modal-field">
          <label>Kategori</label>
          <select value={kategori} onChange={(e) => setKategori(e.target.value)}>
            {Object.entries(CATEGORY_COLORS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="cal-modal-field">
          <label>Beskrivelse</label>
          <textarea value={beskrivelse} onChange={(e) => setBeskrivelse(e.target.value)} placeholder="Valgfri beskrivelse..." rows={3} />
        </div>

        <div className="cal-modal-actions">
          <button className="cal-btn-cancel" onClick={onClose}>Annuller</button>
          <button className="cal-btn-save" onClick={handleSave} disabled={!titel.trim()}>Gem</button>
        </div>
      </div>
    </div>
  );
}

// ─── Vis/slet-modal ───────────────────────────────────────────────────────────
function ViewModal({ event, onClose, onDelete }) {
  const { color, label } = getCategoryStyle(event.extendedProps?.kategori);
  const teamName = event.extendedProps?.teamName;
  const fmt = (d) => d ? new Date(d).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ height: 4, background: color, borderRadius: 4, marginBottom: 14 }} />
        <h3>{event.title}</h3>

        {teamName && (
          <div className="cal-modal-field">
            <label>Hold</label>
            <p>{teamName}</p>
          </div>
        )}

        <div className="cal-modal-field">
          <label>Kategori</label>
          <p>{label}</p>
        </div>

        <div className="cal-modal-row">
          <div className="cal-modal-field"><label>Start</label><p>{fmt(event.start)}</p></div>
          <div className="cal-modal-field"><label>Slut</label><p>{fmt(event.end)}</p></div>
        </div>

        {event.extendedProps?.beskrivelse && (
          <div className="cal-modal-field">
            <label>Beskrivelse</label>
            <p>{event.extendedProps.beskrivelse}</p>
          </div>
        )}

        <div className="cal-modal-actions">
          <button className="cal-btn-delete" onClick={() => onDelete(event.extendedProps?.dbId)}>Slet</button>
          <button className="cal-btn-cancel" onClick={onClose}>Luk</button>
        </div>
      </div>
    </div>
  );
}

// ─── Kalender med hold-vælger ─────────────────────────────────────────────────
function TeamCalendar({ teams }) {
  // Start på "alle hold" hvis man er på flere, ellers det ene hold
  const [selectedTeam, setSelectedTeam] = useState(
    teams && teams.length > 1 ? "all" : (teams?.[0]?.id ?? null)
  );
  const [events, setEvents] = useState([]);
  const [createDate, setCreateDate] = useState(null);
  const [viewEvent, setViewEvent] = useState(null);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "";

  // Hent events – enten for ét hold eller alle samlet
  const loadEvents = useCallback(async (sel, teamList) => {
    try {
      if (sel === "all") {
        // Hent events for alle hold parallelt og slå sammen
        const results = await Promise.all(
          teamList.map((t) =>
            fetch(`${BACKEND_URL}/api/profile/teams/${t.id}/events`, { credentials: "include" })
              .then((r) => r.json())
              .then((data) => {
                const arr = Array.isArray(data) ? data : data.events || [];
                return arr.map((ev) => ({ ...ev, _teamId: t.id, _teamName: t.name }));
              })
              .catch(() => [])
          )
        );
        setEvents(results.flat());
      } else if (sel) {
        const res = await fetch(`${BACKEND_URL}/api/profile/teams/${sel}/events`, { credentials: "include" });
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data.events || [];
        setEvents(arr.map((ev) => ({ ...ev, _teamId: sel, _teamName: teamName(sel) })));
      }
    } catch {
      setEvents([]);
    }
  }, [teams]);

  useEffect(() => { loadEvents(selectedTeam, teams); }, [selectedTeam, teams, loadEvents]);

  // Map til FullCalendar (understøtter både titel/title og start/start_time)
  const mappedEvents = events.map((ev) => {
    const { color } = getCategoryStyle(ev.kategori || ev.category);
    return {
      id: String(ev.id),
      title: ev.titel || ev.title,
      start: ev.start || ev.start_time,
      end: ev.slut || ev.end_time,
      backgroundColor: color,
      borderColor: color,
      extendedProps: {
        dbId: ev.id,
        kategori: ev.kategori || ev.category || "andet",
        beskrivelse: ev.beskrivelse || ev.description || "",
        teamId: ev._teamId,
        teamName: ev._teamName,
      },
    };
  });

  const handleSaveEvent = async (eventData) => {
    // eventData.team_id kommer fra modal-vælgeren
    const targetTeam = eventData.team_id || (selectedTeam !== "all" ? selectedTeam : teams[0]?.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/teams/${targetTeam}/events`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(eventData),
      });
      const result = await res.json();
      if (result.status === "success" || result.id) loadEvents(selectedTeam, teams);
      else alert("Kunne ikke gemme begivenhed");
    } catch {
      alert("Kunne ikke gemme begivenhed.");
    }
    setCreateDate(null);
  };

  const handleDeleteEvent = async (dbId) => {
    if (!dbId) return;
    try {
      await fetch(`${BACKEND_URL}/api/profile/events/${dbId}`, { method: "DELETE", credentials: "include" });
      loadEvents(selectedTeam, teams);
      setViewEvent(null);
    } catch {
      alert("Kunne ikke slette begivenhed.");
    }
  };

  const updateEventTime = async (info) => {
    const dbId = info.event.extendedProps?.dbId;
    if (!dbId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/events/${dbId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start: info.event.start?.toISOString(),
          slut: info.event.end?.toISOString() || info.event.start?.toISOString(),
        }),
      });
      const result = await res.json();
      if (result.status !== "success") info.revert();
    } catch {
      info.revert();
    }
  };

  if (!teams || teams.length === 0) {
    return <p>Du er ikke tilknyttet et hold endnu.</p>;
  }

  return (
    <>
      {createDate && (
        <CreateModal
          date={createDate}
          teams={teams}
          defaultTeam={selectedTeam !== "all" ? selectedTeam : teams[0]?.id}
          onClose={() => setCreateDate(null)}
          onSave={handleSaveEvent}
        />
      )}
      {viewEvent && (
        <ViewModal event={viewEvent} onClose={() => setViewEvent(null)} onDelete={handleDeleteEvent} />
      )}

      <div className="calendar-header-row">
        <h2>Kalender</h2>
        <span className="calendar-hint">Klik på dato for at oprette • Klik på event for detaljer</span>
      </div>

      {teams.length > 1 && (
        <div className="cal-team-tabs">
          <button
            className={`cal-team-tab ${selectedTeam === "all" ? "active" : ""}`}
            onClick={() => setSelectedTeam("all")}
          >
            Alle hold
          </button>
          {teams.map((t) => (
            <button
              key={t.id}
              className={`cal-team-tab ${selectedTeam === t.id ? "active" : ""}`}
              onClick={() => setSelectedTeam(t.id)}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}

      <CalendarLegend />

      <div className="calendar-container">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={window.innerWidth < 768 ? "timeGridWeek" : "dayGridMonth"}
          headerToolbar={{ left: "prev,next", center: "title", right: "dayGridMonth,timeGridWeek" }}
          height="auto"
          locale="da"
          firstDay={1}
          selectable={true}
          editable={true}
          dateClick={(info) => setCreateDate(info.dateStr)}
          eventClick={(info) => setViewEvent(info.event)}
          eventDrop={updateEventTime}
          eventResize={updateEventTime}
          events={mappedEvents}
        />
      </div>
    </>
  );
}

// ─── Hovedkomponent ───────────────────────────────────────────────────────────
export default function ProfileAcademy() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [holdInfo, setHoldInfo] = useState(null);
  const [teams, setTeams] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/user`, { credentials: "include" });
        const data = await res.json();
        if (data.status === "logged_in") {
          setUser(data);
          setEditData({ ...data });
        } else {
          navigate("/login");
        }
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    }

    async function fetchHoldInfo() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/user/holdinfo`, { credentials: "include" });
        const data = await res.json();
        setHoldInfo(data);
        if (Array.isArray(data.teams)) {
          setTeams(data.teams);
        } else if (data.team_id && data.holdnavn) {
          setTeams([{ id: data.team_id, name: data.holdnavn }]);
        }
      } catch {
        setHoldInfo({ status: "error" });
      }
    }

    fetchUser();
    fetchHoldInfo();
  }, [navigate]);

  const saveProfile = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/user`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      const updated = await res.json();
      if (updated.status === "success") {
        setUser({ ...user, ...updated });
        alert("Profil opdateret!");
      } else {
        alert("Kunne ikke opdatere profil");
      }
    } catch {
      alert("Kunne ikke opdatere profil.");
    }
  };

  const payPackage = async () => {
    if (!user?.stripe_price_id) return alert("Ingen pakke valgt");
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/user/checkout`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_id: user.stripe_price_id }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert("Fejl ved Stripe checkout.");
    } catch {
      alert("Kunne ikke starte betaling.");
    }
  };

  if (loading) return <p className="loading-text">Indlæser...</p>;
  if (!user) return null;

  const status = STATUS_MAP[user.payment_status] || { label: "Ikke betalt ❌", className: "unpaid" };

  return (
    <div className="profile-container">

      {/* Header */}
      <div className="profile-header">
        <h1 className="welcome-text">Velkommen - {user.username}</h1>
      </div>

      {/* Personlige oplysninger */}
      <div className="profile-card">
        <h2>Personlige oplysninger</h2>
        {Object.keys(PERSONAL_FIELDS).map((key) => (
          <div key={key} className="form-group">
            <label>{PERSONAL_FIELDS[key]}</label>
            <input
              type="text"
              value={editData[key] || ""}
              readOnly={!EDITABLE_FIELDS.includes(key)}
              onChange={(e) => EDITABLE_FIELDS.includes(key) && setEditData({ ...editData, [key]: e.target.value })}
              className={!EDITABLE_FIELDS.includes(key) ? "readonly-input" : ""}
            />
          </div>
        ))}
        <button className="btn btn-save" onClick={saveProfile}>Gem ændringer</button>
      </div>

      {/* Hold Info */}
      <div className="profile-card">
        <h2>Hold Info</h2>
        {!holdInfo ? (
          <p>Indlæser hold info...</p>
        ) : holdInfo.status === "success" ? (
          <>
            <p>Holdnavn: {holdInfo.holdnavn}</p>
            <h3>Mine spil tider</h3>
            {holdInfo.game_times?.length > 0 ? (
              <ul>
                {holdInfo.game_times.map((gt, idx) => (
                  <li key={idx}>{gt.game_name} - {gt.day_name} {gt.start_time} til {gt.end_time}</li>
                ))}
              </ul>
            ) : <p>Ingen spil tider registreret</p>}
          </>
        ) : holdInfo.status === "no_team" ? (
          <p>Du er ikke tilknyttet et hold.</p>
        ) : (
          <p>Kunne ikke hente hold info fra serveren.</p>
        )}
      </div>

      {/* Pakke */}
      <div className="profile-card package-card">
        <div className="package-header">
          <h2>{user.stripe_product_name || "Support"}</h2>
          {user.payment_status && (
            <span className={`package-status ${status.className}`}>{status.label}</span>
          )}
        </div>
        {user.stripe_price_id && user.payment_status !== "paid" && (
          <button className="btn-pay" onClick={payPackage}>Betal pakke</button>
        )}
        <ul className="package-features">
          <li>🎮 Adgang til holdtræning</li>
          <li>💬 Direkte support</li>
          <li>📅 Eksklusive events</li>
        </ul>
      </div>

      {/* Kalender med hold-vælger */}
      {teams.length > 0 && (
        <div className="profile-card calendar-card">
          <TeamCalendar teams={teams} />
        </div>
      )}

    </div>
  );
}